import BaseComponent from '../base-component/base-component'
import { bodyElement } from '../../components/common/body-element'
import { delay, checkEscKey, getTransitionDuration } from '../../utils/utils'
import {
  getStylePropValue,
  getTemplateFirstChildContent,
} from '../../utils/dom'
import { Nullable } from '../../types/common'

class Modal extends BaseComponent {
  template: Nullable<HTMLTemplateElement>
  element: Nullable<HTMLElement>
  isOpen: boolean
  styles: CSSStyleDeclaration

  constructor() {
    super()

    this.template = this.querySelector(`[data-modal-template="${this.id}"]`)
    this.element = getTemplateFirstChildContent(this.template)

    this.styles = getComputedStyle(this)
  }

  handleKeydown = (event: KeyboardEvent): void => {
    const key = event.key || event.keyCode

    const isEscape = checkEscKey(key)

    if (isEscape) {
      this.hide()
    }
  }

  handleOutsideClick = (event: MouseEvent): void => {
    const overlay = document.getElementById('BodyOverlay')
    const overlayZIndex = parseFloat(getStylePropValue(overlay, 'zIndex'))
    const zIndex = parseFloat(this.styles['zIndex']) - 1
    const isOverlay = event.target === overlay

    const isOutsideClick = isOverlay && overlayZIndex <= zIndex

    if (isOutsideClick && this.isOpen) {
      this.hide()
    }
  }

  show(): void {
    this.mount()

    const videos = this.element.querySelectorAll('video')

    videos.forEach(video => {
      video.play()
    })

    this.focus()

    setTimeout(() => {
      this.setOpenState(true)
    }, 0)

    this.isOpen = true
  }

  hide(): Promise<void> {
    this.setOpenState(false)

    return delay(getTransitionDuration(this.styles)).then(() => {
      this.unmount()

      this.isOpen = false
    })
  }

  toggle(): void {
    if (this.isOpen) {
      this.hide()
    } else {
      this.show()
    }
  }

  mount(): void {
    if (this.element && !this.contains(this.element)) {
      this.appendChild(this.element)
    }
  }

  unmount(): void {
    if (this.element) {
      this.element.remove()
    }
  }

  setOpenState(isOpen: boolean): void {
    const withOverlay = this.hasAttribute('data-with-overlay')

    if (withOverlay) {
      const isImportant = this.hasAttribute('data-overlay-important')
      const overlayOpacity = this.getAttribute('data-overlay-opacity') || 50

      const styles = {
        zIndex: isImportant ? parseFloat(this.styles['zIndex']) - 1 : 3,
        backgroundColor: `var(--gsc-black-color-${overlayOpacity})`,
      }

      isOpen
        ? bodyElement.showOverlay(this.id, styles)
        : delay(getTransitionDuration(this.styles)).then(() => {
            bodyElement.hideOverlay(this.id)
          })
    }

    this.setVisible(isOpen)
    this.setListeners(isOpen ? 'add' : 'remove')

    this.emit(isOpen ? 'show' : 'hide', {})
  }

  setVisible(isVisible: boolean): void {
    this.toggleAttribute('open', isVisible)
  }

  setListeners(action: string): void {
    const withOutsideClick = this.hasAttribute('data-with-close-on-outside')
    const eventListenerSetter = `${action}EventListener`

    if (withOutsideClick) {
      window[eventListenerSetter]('click', this.handleOutsideClick)
    }

    window[eventListenerSetter]('keydown', this.handleKeydown)
  }
}

export default Modal
